import React from "react";

type SlotStatus =
  | "loading"
  | "closed"
  | "past"
  | "no-working-hours"
  | "no-slots"
  | "available";


interface TimeSlotSelectorProps {
  loading: boolean;
  status: SlotStatus;
  timeSlots: string[];
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  selectedDate: string;
  errorMessage?: string | null;
}

const formatSlotTo12Hour = (time: string): string => {
  if (!time) return "";
  if (/[a-zA-Z]/.test(time)) return time;

  const [hStr, mStr] = time.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return time;

  const period = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  const displayMin = m.toString().padStart(2, "0");
  return `${displayHour}:${displayMin} ${period}`;
};

export default function TimeSlotSelector({
  loading,
  status,
  timeSlots,
  selectedTime,
  onTimeSelect,
  selectedDate,
  errorMessage,
}: TimeSlotSelectorProps) {
  if (loading || status === "loading") {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {[...Array(12)].map((_, index) => (
          <div
            key={index}
            className="h-12 bg-gray-200 dark:bg-gray-600 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (status === "closed") {
    const dayName = new Date(selectedDate + "T12:00:00").toLocaleDateString(
      "en-US",
      { weekday: "long" }
    );

    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-700">
        <p className="text-sm text-red-700 dark:text-red-400 text-center">
          🚫 We're closed on {dayName}. Please choose another day.
        </p>
      </div>
    );
  }

  if (status === "no-working-hours") {
    return (
      <div className="p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-700">
        <p className="text-sm text-orange-700 dark:text-orange-400 text-center">
          📅 Working hours are not configured.
        </p>
      </div>
    );
  }

  if (status === "past") {
    return (
      <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
        <p className="text-sm text-blue-700 dark:text-blue-400 text-center">
          ⏰ All remaining reservation times for today have passed. Please choose another date.
        </p>
      </div>
    );
  }

  if (status === "no-slots") {
    return (
      <div className="p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-700">
        <p className="text-sm text-orange-700 dark:text-orange-400 text-center">
          {errorMessage || "⚠️ No reservation slots are available for this date."}
        </p>
      </div>
    );
  }

  if (status === "available" && timeSlots.length > 0) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {timeSlots.map((time) => {
          const isSelected = selectedTime === time;
          const label = formatSlotTo12Hour(time);

          return (
            <button
              key={time}
              type="button"
              onClick={() => onTimeSelect(time)}
              className={`p-3 rounded-lg font-semibold transition-all border-2 ${
                isSelected
                  ? "bg-[#EF4444] border-[#EF4444] text-white"
                  : "border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:border-gray-400"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200">
      <p className="text-sm text-gray-700 dark:text-gray-400 text-center">
        {errorMessage || "No time slots available."}
      </p>
    </div>
  );
}